import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import FOAForm from '../../components/Form/FOAForm';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { FOA, TableColumn } from '../../types';
import { useBranches } from '../../hooks/useBranches';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import {
  useFOAs,
  useCreateFOA,
  useUpdateFOA,
  useDeleteFOA,
} from '../../hooks/useFOAs';

const FOAPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingFOA, setEditingFOA] = useState<FOA | undefined>();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: foas, isLoading, error, refetch } = useFOAs(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);

  const createMutation = useCreateFOA(() => {
    setShowForm(false);
    setEditingFOA(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateFOA(() => {
    setShowForm(false);
    setEditingFOA(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteFOA(() => {
    setTimeout(() => refetch(), 100);
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');

  // branchId holds the branch documentId (relation), older records may hold the numeric id
  const getBranchName = useCallback((branchId: any) => {
    if (!branchId) return '-';
    if (typeof branchId === 'object') return branchId.name || '-';
    const branch = branches.find(
      (b: any) => b.documentId === branchId || String(b.id) === String(branchId)
    );
    return branch ? branch.name : '-';
  }, [branches]);

  const filteredFOAs = useMemo(() => {
    if (!foas) return [];
    return foas.map(foa => ({
      ...foa,
      status: (foa as any).foa_status || foa.status
    })).filter(foa => {
      const branchName = getBranchName(foa.branchId);
      const matchesSearch = !searchTerm || (
        foa.foaNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        foa.englishName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        foa.arabicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branchName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesBranch = branchFilter === 'all' || String(foa.branchId) === String(branchFilter);
      const jsonCurrencies = (foa.amounts || []).map((a: any) => a.currency).filter(Boolean);
      const foaCurrencies = [...jsonCurrencies, foa.currency1 ?? foa.currency, foa.currency2, foa.currency3, foa.currency4, foa.currency5]
        .filter(Boolean)
        .map((c: any) => resolveCurrencyCode(c));
      const matchesCurrency = currencyFilter === 'all' || foaCurrencies.includes(currencyFilter);
      const foaAmount = foa.amounts?.[0]?.amount ?? foa.amount1 ?? foa.amount ?? 0;
      const matchesAmountFrom = !amountFromFilter || foaAmount >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || foaAmount <= parseFloat(amountToFilter);
      return matchesSearch && matchesBranch && matchesCurrency && matchesAmountFrom && matchesAmountTo;
    });
  }, [foas, searchTerm, branchFilter, currencyFilter, amountFromFilter, amountToFilter, getBranchName]);

  const handleExport = () => {
    const exportData = filteredFOAs.map(foa => ({
      'FOA Number': foa.foaNumber,
      'English Name': foa.englishName,
      'Arabic Name': foa.arabicName,
      'Vessel/Headquarters': getBranchName(foa.branchId),
      'Amounts': (foa.amounts && foa.amounts.length > 0)
        ? foa.amounts.map((a: any) => `${a.currency || ''} ${a.amount}`).join(' | ')
        : `${resolveCurrencyCode(foa.currency1 ?? foa.currency)} ${foa.amount1 ?? foa.amount ?? 0}`,
      'Status': foa.status,
      'Assigned To': foa.assignedTo,
      'Issue Date': new Date(foa.issueDate).toLocaleDateString(),
      'Expiry Date': foa.expiryDate ? new Date(foa.expiryDate).toLocaleDateString() : 'N/A'
    }));
    exportToExcel(exportData, `foa_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredFOAs.map(foa => ({
      'FOA Number': foa.foaNumber,
      'English Name': foa.englishName,
      'Arabic Name': foa.arabicName,
      'Vessel/Headquarters': getBranchName(foa.branchId),
      'Amount 1': foa.amount1 ?? foa.amount ?? 0,
      'Currency 1': resolveCurrencyCode(foa.currency1 ?? foa.currency),
      'Status': foa.status,
      'Assigned To': foa.assignedTo,
      'Issue Date': new Date(foa.issueDate).toLocaleDateString(),
      'Expiry Date': foa.expiryDate ? new Date(foa.expiryDate).toLocaleDateString() : 'N/A'
    }));
    printAsExcel(exportData, 'FOA (امر الاسناد)');
  };

  const filters: FilterConfig[] = [
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [{ value: 'all', label: 'All Vessels/HQ' }, ...branches.map((b: any) => ({ value: b.documentId || b.id, label: b.name }))] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [{ value: 'all', label: 'All Currencies' }, { value: 'USD', label: 'USD' }, { value: 'EGP', label: 'EGP' }] },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setBranchFilter('all');
    setCurrencyFilter('all');
    setAmountFromFilter('');
    setAmountToFilter('');
  };

  const columns: TableColumn[] = [
    { key: 'foaNumber', label: 'FOA Number', sortable: true },
    { key: 'englishName', label: 'English Name', sortable: true },
    { key: 'arabicName', label: 'Arabic Name (العربية)', sortable: true },
    { key: 'branchId', label: 'Branch/Vessel', sortable: true, render: (value: string) => getBranchName(value) },
    { key: 'amount1', label: 'Amount 1', sortable: true, render: (_value: number, row: any) => {
      const first = row.amounts?.[0];
      const amt = first?.amount ?? row.amount1 ?? row.amount ?? 0;
      const cur = first?.currency || resolveCurrencyCode(row.currency1 ?? row.currency);
      return `${cur} ${Number(amt).toLocaleString()}`;
    } },
    { key: 'amount2', label: 'Other Amounts', sortable: false, render: (_value: number, row: any) => {
      const others = (row.amounts || []).slice(1);
      if (others.length > 0) {
        return (
          <div className="flex flex-col gap-0.5">
            {others.map((a: any, i: number) => (
              <span key={i}>{a.currency || ''} {Number(a.amount || 0).toLocaleString()}</span>
            ))}
          </div>
        );
      }
      if (!row.amount2 && !row.currency2) return '-';
      return `${row.currency2 ? resolveCurrencyCode(row.currency2) : ''} ${Number(row.amount2 || 0).toLocaleString()}`;
    } },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => {
      let color = 'bg-gray-100 text-gray-800';
      if (value === 'active') color = 'bg-green-100 text-green-800';
      else if (value === 'completed') color = 'bg-blue-100 text-blue-800';
      else if (value === 'inactive') color = 'bg-red-100 text-red-800';
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{value}</span>;
    } },
    { key: 'assignedTo', label: 'Assigned To', sortable: true },
    { key: 'issueDate', label: 'Issue Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'expiryDate', label: 'Expiry Date', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A' },
  ];

  const handleEdit = (foa: FOA) => {
    const foaWithStatus = { ...foa, status: (foa as any).foa_status || foa.status };
    setEditingFOA(foaWithStatus);
    setShowForm(true);
  };

  const handleDelete = async (foa: FOA) => {
    if (!window.confirm(`Are you sure you want to delete FOA ${foa.foaNumber}?`)) return;
    if (!foa?.id) return;
    try {
      await deleteMutation.execute(foa);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingFOA(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<FOA>) => {
    try {
      const apiData: any = { ...data };
      if (editingFOA?.id) {
        const updateData = { ...apiData, documentId: editingFOA.documentId };
        await updateMutation.execute({ id: editingFOA.id, data: updateData });
      } else {
        await createMutation.execute(apiData);
        // Persist the used sequence so the next FOA number advances
        await bumpCodePrefixSequence(apiData.foaNumber);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingFOA(undefined);
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading FOAs. Please try again." onRetry={refetch} entityName="FOAs" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <FOAForm foa={editingFOA} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="FOA Management (امر الاسناد)" description="Manage FOA orders and authorization documents" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search FOAs..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={foas?.length ?? 0}
        filteredCount={filteredFOAs.length}
        entityName="FOA orders"
        onAdd={handleAdd}
        addLabel="Add FOA"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredFOAs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title=""
        searchPlaceholder=""
      />
    </div>
  );
};

export default FOAPage;
